import React from 'react';
import { useIntl } from 'react-intl';
import { AlertTriangle, Ban, CheckCircle2, CircleDashed, Mail, UserPlus, Undo2, XCircle } from 'lucide-react';
import { roleLabelId, statusMeta } from './programAdminModel';
import { OpenTag, StateTag as Tag, SuggestionTag } from './programAdminStyles';

// The colour-coded state of a spot. Colour is never the only signal (WCAG: never colour alone): the
// word is printed, the tag has its own glyph and its own left border, so the six states stay apart
// for colour-blind users and in a greyscale print.
const STATUS_ICON = {
  PROPOSED: CircleDashed,
  INVITED: Mail,
  CONFIRMED: CheckCircle2,
  DECLINED: XCircle,
  WITHDRAWN: Undo2,
  CANCELLED: Ban,
};

export const StateTag = ({ status }) => {
  const intl = useIntl();
  const meta = statusMeta(status);
  const Icon = STATUS_ICON[status] || CircleDashed;
  return (
    <Tag $bg={meta.bg} $fg={meta.fg} $border={meta.border} data-status={status}>
      <Icon size={11} aria-hidden="true" />
      {intl.formatMessage({ id: meta.id, defaultMessage: meta.def })}
    </Tag>
  );
};

// An open spot is the one thing an admin must act on, so it is marked as such next to its state
// (plan §7.7 item 4: "open spots clearly marked").
export const OpenSpotTag = () => {
  const intl = useIntl();
  return (
    <OpenTag>
      <AlertTriangle size={11} aria-hidden="true" />
      {intl.formatMessage({ id: 'programAdmin.needsFilling', defaultMessage: 'Needs filling' })}
    </OpenTag>
  );
};

// A declined spot whose person named a replacement: the name is printed in the tag so the admin
// sees it while scanning the program (a name-only suggestion has no email, the name is enough).
export const SuggestedTag = ({ suggestion }) => {
  const intl = useIntl();
  const who = suggestion.name || suggestion.email;
  return (
    <SuggestionTag data-testid="suggestion-tag" title={who}>
      <UserPlus size={11} aria-hidden="true" />
      {intl.formatMessage({ id: 'programAdmin.suggestion.tag', defaultMessage: 'Replacement suggested' })}: {who}
    </SuggestionTag>
  );
};

export const RoleText = ({ role }) => {
  const intl = useIntl();
  return (
    <>
      {intl.formatMessage({ id: roleLabelId(role), defaultMessage: role.charAt(0) + role.slice(1).toLowerCase() })}
    </>
  );
};
